import { IconProp } from '@fortawesome/fontawesome-svg-core';
import AudienceVisibilityTable from '@go-client/components/AudienceVisibilityTable';
import { CallToActions } from '@go-client/components/BasicForms/CallToActions';
import {
  BubbleAlert,
  BubbleBadge,
  BubbleButton,
  BubbleFormGroup,
  BubbleImageSelector,
  BubbleInput,
  BubbleSelect,
  BubbleTextArea,
  BubbleTypeahead,
  BubbleTypeaheadSimple,
  BubbleCurrency,
  BubblePercentage,
  BubbleHintSelect,
  BubbleCheckbox,
} from '@go-client/components/BubbleUI';
import McmVisibilityTable from '@go-client/components/McmVisibilityTable';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { update, create } from '@go-client/lib/api/eventTemplate';
import { CUSTOM_ICONS } from '@go-client/lib/icons';
import { EVENT_DISPLAY_MAP, EVENT_DISPLAY_TYPES } from '@go-shared/lib/event';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { canModifyMultiPropertyItem } from '@go-shared/lib/permissions';
import { Audience } from '@go-shared/types/audience';
import { DailyProgramCta } from '@go-shared/types/dailyProgram';
import {
  createDefaultTicket,
  createDefaultPackage,
  createDefaultAddon,
  experienceIsRental,
} from '@go-shared/lib/reserve';
import {
  BareEventPropertyInfo,
  BareEventTemplate,
  EventDisplayOption,
} from '@go-shared/types/eventTemplate';
import { Mcm } from '@go-shared/types/mcm';
import {
  ReservableAddon,
  ReservableEventTemplate,
  ReservableExcluded,
  ReservableIncluded,
  ReservablePackage,
  ReservableQuestionField,
  ReservableTicket,
  RESERVABLE_EXPERIENCE_TYPE,
} from '@go-shared/types/reservable';
import { ObjectId, ObjectID } from 'bson';
import {
  Form,
  FormikConfig,
  FormikErrors,
  FormikTouched,
  useFormik,
  yupToFormErrors,
  FormikProps,
  FormikProvider,
} from 'formik';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Yup from 'yup';
import {
  ReservableQuestionFields,
  ReservableTicketFields,
} from '@go-client/components/BasicForms/ReservableTickets';
import { Editor, StaffAllocationsPanel } from '@go-client/components';
import {
  fme,
  safeIdCompare,
  unsafeDeepCopy,
  formatCentsToUSD,
  makeCtaHints,
} from '@go-shared/lib/utils';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import { useBubbleDrawerTools } from '@go-client/components/BubbleUI/BubbleDrawer';
import { Property, PropertyZone } from '@go-shared/types/property';
import { Brand, BrandAttribute, BrandLevel, BrandSegment } from '@go-shared/types/brand';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { STRIPE_TAX_CODES, STRIPE_TAX_CODES_MAP } from '@go-shared/lib/stripe';
import ReserveCopyFromExperience from '../ReserveCopyFromExperience';
import ReservableExperienceTypeSelector from '@go-client/components/ReservableExperienceTypeSelector';

export interface EventTemplateFormikForm {
  properties: BareEventPropertyInfo[];

  title: string;
  description: string;
  images: string[];
  defaultDuration: number;
  interactiveFeedDisplay: EventDisplayOption;
  segment?: string;
  levels: string[];
  attributes: string[];
  mcm?: Mcm;
  audience?: Audience;
  audienceZoneFilter?: string;
  callToActions: DailyProgramCta[];

  notNative: boolean;
  inventoried: boolean;
  initialInventory: number;
  isPrivate: boolean;
  crewOnly: boolean;
  simple: boolean;
  homecoming: boolean;
  allAboard: boolean;
  vip: boolean;
  priced: boolean;
  initialPrice: number;
  reservables: {
    experienceType: RESERVABLE_EXPERIENCE_TYPE;
    marketingDuration: string;
    marketingCapacity: string;
    marketingPrice: string;
    marketingDescription: string;
    meetingPoint: string;
    activityLocation: string;
    included: ReservableIncluded[];
    excluded: ReservableExcluded[];
    requireAtLeastOneAddon: boolean;
    disclaimer: string;
    instructions: string;
    hostPercentage: number;
    gratuityFeePercentage: number;
    gratuityFeeFlatAmountCents: number;
    taxCode?: string;
    bookingWindowDays?: number;
    bookingOpenDays?: number;
    emailSettings: {
      instantEmails: string[];
      summaryEmails: string[];
    };
    externalId?: string;
    autoAssignRental?: boolean;
  };
  reservableTickets: ReservableTicket[];
  reservablePackages: ReservablePackage[];
  reservableAddons: ReservableAddon[];
  reservableFields: ReservableQuestionField[];
  _existingTemplateId?: string;
}

export function GENERATE_INITIAL_VALUES(
  property: Property,
  edit?: BareEventTemplate
): EventTemplateFormikForm {
  const values: EventTemplateFormikForm = {
    properties: edit?.properties ?? [
      {
        property: property._id,
        defaultStaffAllocations: [],
      },
    ],

    title: edit?.title ?? '',
    description: edit?.description ?? '',
    images: edit?.images ?? [],
    defaultDuration: edit?.defaultDuration ?? 30,
    interactiveFeedDisplay: edit?.interactiveFeedDisplay ?? 'show-finish-time',
    segment: edit?.segment ?? undefined,
    levels: edit?.levels ?? [],
    attributes: edit?.attributes ?? [],
    mcm: edit?.mcm,
    audience: edit?.audience,
    callToActions: edit?.callToActions?.map(cta => ({ ...cta })) || [],

    notNative: edit?.notNative ?? false,
    inventoried: edit?.inventoried ?? false,
    initialInventory: edit?.initialInventory ?? 0,
    isPrivate: edit?.isPrivate ?? false,
    crewOnly: edit?.crewOnly ?? false,
    simple: edit?.simple ?? false,
    homecoming: edit?.homecoming ?? false,
    allAboard: edit?.allAboard ?? false,
    vip: edit?.vip ?? false,
    priced: edit?.priced ?? false,
    initialPrice: edit?.initialPrice ?? 0,
    reservables: {
      experienceType: edit?.reservables?.experienceType ?? 'ticket',
      marketingDuration: edit?.reservables?.marketingDuration ?? '',
      marketingCapacity: edit?.reservables?.marketingCapacity ?? '',
      marketingPrice: edit?.reservables?.marketingPrice ?? '',
      marketingDescription: edit?.reservables?.marketingDescription ?? '',
      meetingPoint: edit?.reservables?.meetingPoint ?? '',
      activityLocation: edit?.reservables?.activityLocation ?? '',
      included: edit?.reservables?.included ?? [],
      excluded: edit?.reservables?.excluded ?? [],
      requireAtLeastOneAddon: edit?.reservables?.requireAtLeastOneAddon ?? false,
      disclaimer: edit?.reservables?.disclaimer ?? '',
      instructions: edit?.reservables?.instructions ?? '',
      hostPercentage: edit?.reservables?.hostPercentage ?? 0,
      gratuityFeePercentage: edit?.reservables?.gratuityFeePercentage ?? 0,
      gratuityFeeFlatAmountCents: edit?.reservables?.gratuityFeeFlatAmountCents ?? 0,
      taxCode: edit?.reservables?.taxCode ?? undefined,
      externalId: edit?.reservables?.externalId ?? undefined,
      bookingWindowDays: edit?.reservables?.bookingWindowDays ?? undefined,
      bookingOpenDays: edit?.reservables?.bookingOpenDays ?? undefined,
      autoAssignRental: edit?.reservables?.autoAssignRental ?? false,
      emailSettings: {
        instantEmails: edit?.reservables?.emailSettings?.instantEmails ?? [],
        summaryEmails: edit?.reservables?.emailSettings?.summaryEmails ?? [],
      },
    },
    reservableTickets: edit?.reservables?.tickets ?? [],
    reservablePackages: edit?.reservables?.packages ?? [],
    reservableAddons: edit?.reservables?.addons ?? [],
    reservableFields: edit?.reservables?.fields ?? [],
    _existingTemplateId: edit?._id ?? '',
  };

  return values;
}

function validateSignatureFieldSettings(
  this: Yup.TestContext,
  value: Partial<ReservableQuestionField> | undefined
) {
  if (value?.type !== 'signature') return true;

  const settings = value.settings as { content?: string; checkboxLabel?: string } | undefined;

  if (!settings?.content) {
    return this.createError({
      path: `${this.path}.settings.content`,
      message: 'Agreement text is required',
    });
  }

  if (!settings?.checkboxLabel) {
    return this.createError({
      path: `${this.path}.settings.checkboxLabel`,
      message: 'Checkbox label is required',
    });
  }

  return true;
}

export function buildFormik(
  property: Property,
  edit?: BareEventTemplate,
  onSubmit?: FormikConfig<EventTemplateFormikForm>['onSubmit']
) {
  const formik = useFormik({
    initialValues: GENERATE_INITIAL_VALUES(property, edit),
    validate: async values => {
      let errors: FormikErrors<EventTemplateFormikForm> = {};

      const basicSchema = Yup.object({
        title: Yup.string().required('Required'),
        defaultDuration: Yup.number()
          .min(1, 'Must be 1 or more')
          .required('Required')
          .typeError('Must be a number'),
        initialPrice: Yup.number()
          .min(0, 'Must be 0 or more')
          .required('Required')
          .typeError('Must be a number'),
        callToActions: Yup.array().of(
          Yup.object({
            label: Yup.string().required('Required'),
            link: Yup.string().required('Required'),
          })
        ),
        reservableTickets: Yup.array().of(
          Yup.object({
            name: Yup.string().required('Required'),
            price: Yup.number()
              .min(0, 'Must be 0 or more')
              .required('Required')
              .typeError('Must be a number'),
            fields: Yup.array().of(
              Yup.object({
                label: Yup.string().required('Required'),
              }).test('signature-settings', 'Required', validateSignatureFieldSettings)
            ),
          })
        ),
        reservablePackages: Yup.array().of(
          Yup.object({
            name: Yup.string().required('Required'),
            price: Yup.number()
              .min(0, 'Must be 0 or more')
              .required('Required')
              .typeError('Must be a number'),
          })
        ),
        reservableAddons: Yup.array().of(
          Yup.object({
            name: Yup.string().required('Required'),
            price: Yup.number()
              .min(0, 'Must be 0 or more')
              .required('Required')
              .typeError('Must be a number'),
          })
        ),
        reservableFields: Yup.array().of(
          Yup.object({
            label: Yup.string().required('Required'),
          }).test('signature-settings', 'Required', validateSignatureFieldSettings)
        ),
      });

      try {
        await basicSchema.validate(values, { abortEarly: false });
      } catch (err) {
        const t = yupToFormErrors<EventTemplateFormikForm>(err);
        errors = { ...errors, ...t };
      }

      return errors;
    },
    onSubmit: onSubmit || (() => {}),
  });

  return formik;
}

export interface EventTemplateFormikMode {
  type: 'create' | 'edit' | 'view' | 'filter';
  edit?: BareEventTemplate;
  staffTemplates: StaffTemplate[];
}

export interface EvtTmplFormikExtState {
  values: EventTemplateFormikForm;
  disabled: boolean;
}
export interface EventTemplateFormikProps {
  mode: EventTemplateFormikMode;
  actionsElem?: HTMLDivElement;
  actionsClassName?: string;
  actionsAppearance?: 'drawer-float';
  subnavClassName?: string;

  onSubmit?: (template: BareEventTemplate) => void;
  onStateUpdate?: (state: EvtTmplFormikExtState) => void;
}

export interface EventTemplateFormikRef {
  reset: () => void;
  submit: () => void;
}

export function eventFormikValuesToNewEvent(
  values: EventTemplateFormikForm,
  property: Property,
  edit?: BareEventTemplate
): ReservableEventTemplate {
  let newEventTmpl: ReservableEventTemplate = {
    ...(edit || {
      _id: new ObjectID().toHexString(),
      source: property._id,
      brand: property.brand._id,
      incomplete: false,
      softConflict: false,
    }),
    properties: values.properties,
    images: values.images,
    title: values.title,
    description: values.description,
    segment: values.segment,
    levels: values.levels,
    attributes: values.attributes,
    defaultDuration: values.defaultDuration,
    interactiveFeedDisplay: values.interactiveFeedDisplay,
    callToActions: values.callToActions,
    mcm: values.mcm,
    audience: values.audience,

    isPrivate: values.isPrivate,
    notNative: values.notNative,
    inventoried: values.inventoried,
    initialInventory: values.initialInventory,
    priced: values.priced,
    initialPrice: values.initialPrice,
    crewOnly: values.crewOnly,
    simple: values.simple,
    homecoming: values.homecoming,
    allAboard: values.allAboard,
    vip: values.vip,

    reservables: {
      ...(edit?.reservables || {
        _id: new ObjectID().toHexString(),
      }),
      experienceType: values.reservables.experienceType,
      marketingDuration: values.reservables.marketingDuration,
      marketingCapacity: values.reservables.marketingCapacity,
      marketingPrice: values.reservables.marketingPrice,
      marketingDescription: values.reservables.marketingDescription,
      included: values.reservables.included,
      excluded: values.reservables.excluded,
      meetingPoint: values.reservables.meetingPoint,
      activityLocation: values.reservables.activityLocation,
      fields: values.reservableFields,
      tickets: values.reservableTickets,
      packages: values.reservablePackages,
      addons: values.reservableAddons,
      requireAtLeastOneAddon: values.reservables.requireAtLeastOneAddon,
      disclaimer: values.reservables.disclaimer,
      instructions: values.reservables.instructions,
      hostPercentage: values.reservables.hostPercentage,
      gratuityFeePercentage: values.reservables.gratuityFeePercentage,
      gratuityFeeFlatAmountCents: values.reservables.gratuityFeeFlatAmountCents,
      taxCode: values.reservables.taxCode,
      externalId: values.reservables.externalId || undefined,
      bookingWindowDays: values.reservables.bookingWindowDays,
      bookingOpenDays: values.reservables.bookingOpenDays,
      emailSettings: values.reservables.emailSettings,
      autoAssignRental: values.reservables.autoAssignRental,
    },
  };

  return newEventTmpl;
}

type EventTemplateFormikTab = 'main' | 'reservables' | 'staff';
type EventTemplateFormikSubTab = 'none' | 'experienceType' | 'tickets' | 'packages' | 'addons';

function EventTemplateFormik(
  props: EventTemplateFormikProps,
  ref: React.ForwardedRef<EventTemplateFormikRef>
) {
  const { user } = useUserInfo();
  const serverInfo = useServerInfo();
  const { brand } = serverInfo;
  const { activeProperty: property } = useActiveProperty();
  const subTabDrawerRef = React.useRef<HTMLDivElement>(null);
  const { setVerifyClose } = useBubbleDrawerTools();

  const formik = buildFormik(property, props.mode.edit, onFormSubmit);

  useEffect(() => {
    formik.resetForm({
      values: GENERATE_INITIAL_VALUES(property, props.mode.edit),
    });
  }, [props.mode.edit]);

  const { values, setFieldValue, handleSubmit, dirty } = formik;

  useEffect(() => {
    setVerifyClose(dirty);
  }, [dirty]);

  const actionClasses = ['mt-4'];
  if (props.actionsAppearance === 'drawer-float')
    actionClasses.push('sticky bottom-0 z-20 -mx-6 p-6 pt-4 form-blur flex items-center');
  else if (props.actionsAppearance === 'hidden') actionClasses.push('hidden');
  if (props.actionsClassName) actionClasses.push(props.actionsClassName);

  const subnavClasses = ['flex items-center mb-6'];
  if (props.subnavClassName) subnavClasses.push(props.subnavClassName);

  async function onFormSubmit(values: EventTemplateFormikForm) {
    let newEventTmpl = eventFormikValuesToNewEvent(
      values,
      property,
      props.mode.edit
    ) as BareEventTemplate;

    switch (props.mode.type) {
      case 'create':
        const { _id, ...rest } = newEventTmpl;
        newEventTmpl = await create(rest);
        break;
      case 'edit':
        newEventTmpl = await update(newEventTmpl);
        break;
    }

    if (props.onSubmit) props.onSubmit(newEventTmpl);
  }

  const [activeTab, setActiveTab] = useState<EventTemplateFormikTab>('main');
  const [activeSubTab, setActiveSubTab] = useState<EventTemplateFormikSubTab>('none');

  const { toggleScroll } = useBubbleDrawerTools();

  useEffect(() => {
    toggleScroll(activeSubTab === 'none');
  }, [activeSubTab]);

  const locked =
    ['create', 'edit'].includes(props.mode.type) &&
    !canModifyMultiPropertyItem(user, serverInfo, property._id, props.mode.edit);
  const readOnly = props.mode.type === 'view' || locked;
  const disabled = readOnly || locked || formik.isSubmitting || !formik.isValid || !dirty;

  const currentPropStaffAlloc = (
    values.properties.find(p => p.property === property._id)?.defaultStaffAllocations ?? []
  )
    .map(sta => ({
      ...sta,
      template: props.mode.staffTemplates.find(s => safeIdCompare(s._id, sta.template)),
    }))
    .filter(notUndefined);

  const formActions = locked ? (
    <></>
  ) : (
    <div className="flex items-center w-full">
      <div></div>
      <div className="ml-auto" />
      <BubbleButton disabled={disabled} variant="navy" type="reset" inverse icon="rotate-left">
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        type="submit"
        disabled={disabled}
        icon={props.mode.type === 'edit' ? 'floppy-disk' : 'sparkles'}
      >
        {props.mode.type === 'edit' ? 'Save Changes' : 'Create Event'}
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem
    ? createPortal(formActions, props.actionsElem)
    : formActions;

  useImperativeHandle(ref, () => ({
    reset: () => formik.resetForm(),
    submit: () => formik.submitForm(),
  }));

  useEffect(() => {
    if (props.onStateUpdate)
      props.onStateUpdate({
        values: formik.values,
        disabled,
      });
  }, [formik.values, disabled]);

  const segmentsMap = mapBy(property.brand.segments, '_id');
  const levelsMap = mapBy(property.brand.levels, '_id');
  const attributesMap = mapBy(property.brand.attributes, '_id');
  const zonesMap = mapBy(property.zones, '_id');

  function subtabScrollToBottom() {
    setTimeout(() => subTabDrawerRef.current?.scrollTo({ top: 99999, behavior: 'smooth' }));
  }

  return (
    <FormikProvider value={formik}>
      <div className={subnavClasses.join(' ')}>
        <BubbleButton
          block
          appearance="squared"
          variant={activeTab === 'main' ? 'navy' : 'smoke'}
          onClick={() => setActiveTab('main')}
        >
          Details
        </BubbleButton>
        {serverInfo.settings.ALLOW_RESERVE_MANAGEMENT && (
          <BubbleButton
            block
            appearance="squared"
            variant={activeTab === 'reservables' ? 'navy' : 'smoke'}
            onClick={() => setActiveTab('reservables')}
          >
            Reserve
          </BubbleButton>
        )}
        <BubbleButton
          block
          appearance="squared"
          variant={activeTab === 'staff' ? 'navy' : 'smoke'}
          onClick={() => setActiveTab('staff')}
        >
          Staff
        </BubbleButton>
      </div>
      <Form onSubmit={handleSubmit}>
        {locked && (
          <BubbleAlert className="mb-3" variant="ocean">
            <span className="font-semibold">This template cannot be modified.</span> It may be a
            brand level event or you lack library permissions.
          </BubbleAlert>
        )}
        {activeTab === 'main' && (
          <EventTemplateFormikMainTab
            type={props.mode.type}
            formik={formik}
            readOnly={readOnly}
            property={property}
            brand={brand}
            segmentsMap={segmentsMap}
            levelsMap={levelsMap}
            attributesMap={attributesMap}
            zonesMap={zonesMap}
          />
        )}
        {activeTab === 'reservables' && (
          <EventTemplateFormikReservablesTab
            type={props.mode.type}
            formik={formik}
            readOnly={readOnly}
            onSubItemCardClick={setActiveSubTab}
          />
        )}
        {activeTab === 'staff' && (
          <StaffAllocationsPanel
            isLocked={readOnly}
            eventDuration={values.defaultDuration}
            staffAllocations={currentPropStaffAlloc}
            staffTemplates={props.mode.staffTemplates}
            onChange={staffAllocations => {
              // this object is too nested to let formik track it properly, so we have to do this
              const newPropInfos = unsafeDeepCopy(values.properties);

              let propInfo = newPropInfos.find(p => p.property === property._id);
              if (!propInfo) return;

              propInfo.defaultStaffAllocations = staffAllocations.map(sa => ({
                ...sa,
                template: sa.template ? sa.template._id : '',
              }));

              setFieldValue('properties', newPropInfos);
            }}
          />
        )}
        {props.mode.type !== 'view' && (
          <div className={actionClasses.join(' ')}>{portalButtons}</div>
        )}
        <div
          className="absolute top-0 left-0 w-full bg-black opacity-25 cursor-pointer transition-transform"
          onClick={() => setActiveSubTab('none')}
          style={{
            transform: `translateX(${activeSubTab === 'none' ? '100%' : '0'})`,
            zIndex: 21,
            height: '100vh',
          }}
        />
        <div
          ref={subTabDrawerRef}
          className="absolute left-0 bottom-0 z-10 w-full p-6 pb-0 overflow-y-auto bg-eggshell transition-transform"
          style={{
            top: '6vh',
            transform: `translateX(${activeSubTab === 'none' ? '100%' : '0'})`,
            pointerEvents: activeSubTab === 'none' ? 'none' : 'auto',
            height: 'calc(100vh - 6vh)',
            zIndex: 22,
          }}
        >
          {activeSubTab === 'experienceType' && (
            <div>
              <div className="flex items-start mb-5">
                <h2 className="text-2xl text-navy">Experience Type</h2>
                <BubbleButton
                  onClick={() => setActiveSubTab('none')}
                  className="ml-auto"
                  variant="navy"
                  inverse
                  icon="xmark-large"
                  iconSize="xl"
                />
              </div>
              <ReservableExperienceTypeSelector
                experienceType={values.reservables.experienceType}
                modeType={props.mode.type}
                disabled={readOnly}
                onValue={nextType => setFieldValue('reservables.experienceType', nextType)}
                autoAssignRental={values.reservables.autoAssignRental}
                onAutoAssignRental={val => setFieldValue('reservables.autoAssignRental', val)}
              />
              {props.mode.type !== 'view' && (
                <div className={actionClasses.join(' ')}>
                  <div className="ml-auto" />
                  <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                    Close & Continue
                  </BubbleButton>
                </div>
              )}
            </div>
          )}
          {activeSubTab === 'tickets' && (
            <div>
              <div className="flex items-start mb-5">
                <h2 className="text-2xl text-navy">Tickets</h2>
                <BubbleButton
                  onClick={() => setActiveSubTab('none')}
                  className="ml-auto"
                  variant="navy"
                  inverse
                  icon="xmark-large"
                  iconSize="xl"
                />
              </div>
              <EventTemplateFormikTicketsSubTab
                formik={formik}
                type={props.mode.type}
                readOnly={readOnly}
                experienceType={values.reservables.experienceType}
                onAddNewTickets={subtabScrollToBottom}
              />
              {props.mode.type !== 'view' && (
                <div className={actionClasses.join(' ')}>
                  <div className="ml-auto" />
                  <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                    Close & Continue
                  </BubbleButton>
                </div>
              )}
            </div>
          )}
          {activeSubTab === 'packages' && (
            <div>
              <div className="flex items-start mb-5">
                <h2 className="text-2xl text-navy">Packages</h2>
                <BubbleButton
                  onClick={() => setActiveSubTab('none')}
                  className="ml-auto"
                  variant="navy"
                  inverse
                  icon="xmark-large"
                  iconSize="xl"
                />
              </div>
              <EventTemplateFormikPackagesSubTab
                formik={formik}
                type={props.mode.type}
                readOnly={readOnly}
                onAddNewPackages={subtabScrollToBottom}
              />
              {props.mode.type !== 'view' && (
                <div className={actionClasses.join(' ')}>
                  <div className="ml-auto" />
                  <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                    Close & Continue
                  </BubbleButton>
                </div>
              )}
            </div>
          )}
          {activeSubTab === 'addons' && (
            <div>
              <div className="flex items-start mb-5">
                <h2 className="text-2xl text-navy">Addons</h2>
                <BubbleButton
                  onClick={() => setActiveSubTab('none')}
                  className="ml-auto"
                  variant="navy"
                  inverse
                  icon="xmark-large"
                  iconSize="xl"
                />
              </div>
              <EventTemplateFormikAddonsSubTab
                formik={formik}
                type={props.mode.type}
                readOnly={readOnly}
                onAddNewAddons={subtabScrollToBottom}
              />
              {props.mode.type !== 'view' && (
                <div className={actionClasses.join(' ')}>
                  <div className="ml-auto" />
                  <BubbleButton variant="navy" onClick={() => setActiveSubTab('none')}>
                    Close & Continue
                  </BubbleButton>
                </div>
              )}
            </div>
          )}
        </div>
      </Form>
    </FormikProvider>
  );
}

interface EventTemplateFormikMainTabProps {
  property: Property;
  brand: Brand;
  formik: FormikProps<EventTemplateFormikForm>;
  type: 'create' | 'edit' | 'view' | 'filter';
  readOnly?: boolean;
  disabled?: boolean;
  segmentsMap?: Record<string, BrandSegment>;
  levelsMap?: Record<string, BrandLevel>;
  attributesMap?: Record<string, BrandAttribute>;
  zonesMap?: Record<string, PropertyZone>;
  coreFieldsOnly?: boolean;
}

export function EventTemplateFormikMainTab(props: EventTemplateFormikMainTabProps) {
  const {
    type,
    readOnly,
    disabled,
    formik,
    property,
    brand,
    coreFieldsOnly,
    segmentsMap,
    levelsMap,
    attributesMap,
    zonesMap,
  } = props;
  const { values, handleChange, handleBlur, setFieldValue, errors, touched } = formik;

  const ctaHints = values._existingTemplateId
    ? makeCtaHints(brand, { go_event: values._existingTemplateId })
    : undefined;

  return (
    <fieldset disabled={disabled} className="grid grid-cols-2 gap-4">
      {type !== 'filter' && (
        <BubbleFormGroup label="Images" className="col-span-2">
          <BubbleImageSelector
            maxItems={3}
            readOnly={readOnly}
            value={values.images || []}
            onValue={images => setFieldValue('images', images)}
          />
        </BubbleFormGroup>
      )}
      <BubbleFormGroup
        label="Title"
        error={['edit', 'create'].includes(type) && fme(errors, touched, 'title')}
        className="col-span-2"
        asterisk
      >
        <BubbleInput
          block
          type="text"
          formik={[formik, 'title']}
          readOnly={readOnly}
          value={values.title}
          placeholder="E.g. 'Sunset Yoga'"
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Description" className="col-span-2">
        <BubbleTextArea
          block
          name="description"
          readOnly={readOnly}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.description}
          subtitle="Short text that describes the event. It is shown on various places across the guest experience."
        />
      </BubbleFormGroup>
      {segmentsMap && (
        <BubbleFormGroup label="Segment" className="col-span-2">
          <BubbleSelect
            block
            allowUndefined
            labelExtractor={id => segmentsMap[id].name}
            options={property.brand.segments.map(s => s._id)}
            name="segment"
            readOnly={readOnly}
            onValue={val => setFieldValue('segment', val)}
            onBlur={handleBlur}
            value={values.segment}
            subtitle="Guests can filter experiences by segment."
          />
        </BubbleFormGroup>
      )}
      {levelsMap && (
        <BubbleFormGroup label="Levels" className="col-span-2">
          <BubbleTypeaheadSimple
            block
            placeholder="Search..."
            labelExtractor={id => levelsMap[id].name}
            value={values.levels}
            options={property.brand.levels.map(l => l._id)}
            readOnly={readOnly}
            onValue={val => setFieldValue('levels', val)}
            menuClassName="max-h-80 overflow-y-auto"
            subtitle="For internal use only."
          />
        </BubbleFormGroup>
      )}
      {attributesMap && (
        <BubbleFormGroup label="Attributes" className="col-span-2">
          <BubbleTypeaheadSimple
            block
            placeholder="Search..."
            labelExtractor={id => attributesMap[id].name}
            value={values.attributes}
            options={property.brand.attributes.map(a => a._id)}
            readOnly={readOnly}
            onValue={val => setFieldValue('attributes', val)}
            menuClassName="max-h-80 overflow-y-auto"
            subtitle="Guests can filter experiences by attribute."
          />
        </BubbleFormGroup>
      )}
      {type !== 'filter' && !coreFieldsOnly && (
        <>
          <BubbleFormGroup
            label="Default Duration (Minutes)"
            error={['edit', 'create'].includes(type) && fme(errors, touched, 'defaultDuration')}
            asterisk
          >
            <div className={`flex items-center ${!readOnly ? 'bg-white rounded shadow' : ''}`}>
              {!readOnly && (
                <BubbleButton
                  icon="minus"
                  variant="white"
                  onClick={() =>
                    setFieldValue('defaultDuration', Math.max(0, values.defaultDuration - 15))
                  }
                />
              )}
              <BubbleInput
                block
                className="flex-grow"
                type="number"
                min={0}
                formik={[formik, 'defaultDuration']}
                appearance="bare"
                inputClassName="stretched-link py-2 px-1 text-center"
                readOnly={readOnly}
                value={values.defaultDuration}
              />
              {!readOnly && (
                <BubbleButton
                  icon="plus"
                  variant="white"
                  onClick={() => setFieldValue('defaultDuration', values.defaultDuration + 15)}
                />
              )}
            </div>
          </BubbleFormGroup>
          <BubbleFormGroup label="Display Options">
            <BubbleSelect
              block
              allowUndefined
              undefinedLabel="Any"
              labelExtractor={id => EVENT_DISPLAY_MAP[id].label}
              options={EVENT_DISPLAY_TYPES}
              name="interactiveFeedDisplay"
              readOnly={readOnly}
              onValue={val => setFieldValue('interactiveFeedDisplay', val)}
              onBlur={handleBlur}
              value={values.interactiveFeedDisplay}
            />
          </BubbleFormGroup>
        </>
      )}
      {type !== 'filter' && (
        <BubbleFormGroup className="col-span-2" label="Call to Actions">
          <CallToActions type="formik" formik={formik} readOnly={readOnly} hints={ctaHints} />
        </BubbleFormGroup>
      )}
      {type !== 'filter' ? (
        <div className="col-span-2 grid grid-cols-2 gap-4">
          <BubbleFormGroup label="Endpoints">
            <McmVisibilityTable
              isTemplate
              sourceMcm={values.mcm}
              parentMcm={undefined}
              onUpdateMcm={m => setFieldValue('mcm', m)}
              readOnly={readOnly}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label="Audience">
            <AudienceVisibilityTable
              isTemplate
              sourceAud={values.audience}
              parentAud={undefined}
              onUpdateAudience={m => setFieldValue('audience', m)}
              readOnly={readOnly}
            />
          </BubbleFormGroup>
        </div>
      ) : (
        <>
          {zonesMap && (
            <BubbleFormGroup label="Audience" className="col-span-2">
              <BubbleSelect
                block
                allowUndefined
                undefinedLabel="All"
                labelExtractor={id => zonesMap[id].name}
                value={values.audienceZoneFilter}
                options={property.zones.map(z => z._id)}
                onValue={val => setFieldValue('audienceZoneFilter', val)}
              />
            </BubbleFormGroup>
          )}
        </>
      )}
      {type !== 'filter' && !coreFieldsOnly && (
        <div className="col-span-2">
          <BubbleFormGroup label="Miscellaneous">
            <div className="flex space-x-4 items-start">
              <div className="flex-grow grid grid-cols-1 gap-1">
                <BubbleCheckbox
                  formik={[formik, 'isPrivate']}
                  readOnly={readOnly}
                  checked={!!values.isPrivate}
                  label="Private"
                />
                <BubbleCheckbox
                  formik={[formik, 'notNative']}
                  readOnly={readOnly}
                  checked={!!values.notNative}
                  label="Foreign Language"
                />
                <BubbleCheckbox
                  formik={[formik, 'inventoried']}
                  readOnly={readOnly}
                  checked={!!values.inventoried}
                  label="Inventoried"
                />
                {values.inventoried && (
                  <BubbleFormGroup label="Initial Inventory" className="mb-2 mt-1">
                    <BubbleInput
                      block
                      type="number"
                      min={0}
                      formik={[formik, 'initialInventory']}
                      readOnly={readOnly}
                      value={values.initialInventory}
                    />
                  </BubbleFormGroup>
                )}
                <BubbleCheckbox
                  readOnly={readOnly}
                  formik={[formik, 'priced']}
                  checked={!!values.priced}
                  label="Priced"
                />
                {values.priced && (
                  <BubbleFormGroup
                    label="Initial Inventory"
                    error={
                      ['edit', 'create'].includes(type) && fme(errors, touched, 'initialPrice')
                    }
                    className="mb-2 mt-1"
                  >
                    <BubbleInput
                      block
                      type="number"
                      min={0}
                      formik={[formik, 'initialPrice']}
                      readOnly={readOnly}
                      value={values.initialPrice}
                    />
                  </BubbleFormGroup>
                )}
              </div>
              <div className="flex-grow grid grid-cols-1 gap-1">
                <BubbleCheckbox
                  formik={[formik, 'crewOnly']}
                  readOnly={readOnly}
                  checked={!!values.crewOnly}
                  label="Internal Only"
                />
                <BubbleCheckbox
                  formik={[formik, 'simple']}
                  readOnly={readOnly}
                  checked={!!values.simple}
                  label="Simple Event"
                />
                <BubbleCheckbox
                  formik={[formik, 'homecoming']}
                  readOnly={readOnly}
                  checked={!!values.homecoming}
                  label="Homecoming"
                />
                <BubbleCheckbox
                  formik={[formik, 'allAboard']}
                  readOnly={readOnly}
                  checked={!!values.allAboard}
                  label="All Aboard"
                />
                <BubbleCheckbox
                  formik={[formik, 'vip']}
                  readOnly={readOnly}
                  checked={!!values.vip}
                  label="VIP"
                />
              </div>
            </div>
          </BubbleFormGroup>
        </div>
      )}
    </fieldset>
  );
}

interface EventTemplateFormikReserveTabProps {
  formik: FormikProps<EventTemplateFormikForm>;
  type: 'create' | 'edit' | 'view' | 'filter';
  readOnly?: boolean;
  disabled?: boolean;
  onSubItemCardClick?: (tab: EventTemplateFormikSubTab) => void;
}

export function EventTemplateFormikReservablesTab(props: EventTemplateFormikReserveTabProps) {
  const { readOnly, disabled, formik, onSubItemCardClick } = props;
  const { values, handleChange, handleBlur, setFieldValue } = formik;

  const includedOpts = values.reservables.included.map(ri => ({
    key: ri._id,
    refKey: ri._id,
    item: ri,
  }));

  const excludedOpts = values.reservables.excluded.map(re => ({
    key: re._id,
    refKey: re._id,
    item: re,
  }));

  const instantEmailOpts = (values.reservables.emailSettings?.instantEmails ?? []).map(
    (email, idx) => ({ key: String(idx), item: email })
  );

  const summaryEmailOpts = (values.reservables.emailSettings?.summaryEmails ?? []).map(
    (email, idx) => ({ key: String(idx), item: email })
  );

  const reservableFieldErrors = formik.errors.reservableFields as
    | FormikErrors<ReservableQuestionField>[]
    | undefined;

  const reservableFieldTouched = formik.touched.reservableFields as
    | FormikTouched<ReservableQuestionField>[]
    | undefined;

  const largeCards: Array<{
    key: EventTemplateFormikSubTab;
    title: string;
    icon: IconProp;
    hasErrors: boolean;
    summary: string;
    className?: string;
  }> = [
    {
      key: 'experienceType',
      title: 'Experience Type',
      icon: 'layer-group',
      hasErrors: false,
      summary: experienceIsRental(values.reservables.experienceType)
        ? 'Rentals <span class="italic text-slate">(Guests reserve a specific item)</span>'
        : 'Tickets <span class="italic text-slate">(Guests reserve spots/capacity)</span>',
      className: 'col-span-3',
    },
    {
      key: 'tickets',
      title: 'Tickets',
      icon: CUSTOM_ICONS['solid-ticket-pen'],
      hasErrors: !!formik.errors.reservableTickets,
      summary:
        values.reservableTickets
          .map(rt => `${rt.name} (${formatCentsToUSD(rt.price)})`)
          .join('<br/>') || '<span class="italic text-smoke">None</span>',
    },
    {
      key: 'packages',
      title: 'Packages',
      icon: CUSTOM_ICONS['solid-box-open-pen'],
      hasErrors: !!formik.errors.reservablePackages,
      summary:
        values.reservablePackages
          .map(rp => `${rp.name} (${formatCentsToUSD(rp.price)})`)
          .join('<br/>') || '<span class="italic text-smoke">None</span>',
    },
    {
      key: 'addons',
      title: 'Addons',
      icon: CUSTOM_ICONS['solid-puzzle-piece-pen'],
      hasErrors: !!formik.errors.reservableAddons,
      summary:
        values.reservableAddons
          .map(ra => `${ra.name} (${formatCentsToUSD(ra.price)})`)
          .join('<br/>') || '<span class="italic text-smoke">None</span>',
    },
  ];

  return (
    <fieldset disabled={disabled} className="grid grid-cols-2 gap-4">
      {onSubItemCardClick && (
        <div className="col-span-2 grid grid-cols-3 gap-4">
          {largeCards.map(card => (
            <div
              key={card.key}
              className={`bg-white rounded overflow-clip shadow ${card.className ?? ''}`}
            >
              <div className="p-1">
                <BubbleButton
                  variant="white"
                  className="text-left border border-navy"
                  block
                  onClick={() => onSubItemCardClick?.(card.key)}
                  iconSuffix={card.icon}
                  iconClasses="ml-auto"
                  contentClassName="flex items-center text-base"
                >
                  {card.title}
                </BubbleButton>
              </div>
              <hr />
              {card.hasErrors ? (
                <BubbleAlert className="rounded-0-i" variant="banana">
                  There are errors in this section. Please review before submitting.
                </BubbleAlert>
              ) : (
                <div
                  className="p-3 h-24 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: card.summary }}
                ></div>
              )}
            </div>
          ))}
        </div>
      )}
      <BubbleFormGroup label="Price">
        <BubbleInput
          block
          type="text"
          formik={[formik, 'reservables.marketingPrice']}
          value={values.reservables.marketingPrice}
          readOnly={readOnly}
          placeholder="E.g. 'From $99 per ticket', 'Starting at $45', etc."
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Duration">
        <BubbleInput
          block
          type="text"
          formik={[formik, 'reservables.marketingDuration']}
          value={values.reservables.marketingDuration}
          readOnly={readOnly}
          placeholder="E.g. '1 hour', '30 minutes', etc."
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Capacity">
        <BubbleInput
          block
          type="text"
          formik={[formik, 'reservables.marketingCapacity']}
          value={values.reservables.marketingCapacity}
          readOnly={readOnly}
          placeholder="E.g. '10 people', 'Varies', etc."
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Experience ID">
        <BubbleInput
          type="text"
          block
          className="flex-grow"
          name="reservables.externalId"
          readOnly={readOnly}
          value={values.reservables.externalId ?? ''}
          onValue={val => setFieldValue('reservables.externalId', val || undefined)}
          onBlur={handleBlur}
          placeholder="E.g. 'EXP-001'"
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Experience Details" className="col-span-2">
        <Editor
          textareaName="reservables.marketingDescription"
          value={values.reservables.marketingDescription || ''}
          onValue={val => {
            handleChange({
              target: {
                name: `reservables.marketingDescription`,
                value: val,
              },
            });
          }}
          onBlur={evt => {
            evt.target.name = 'reservables.marketingDescription';
            handleBlur(evt);
          }}
          mode="text-link"
          disabled={readOnly}
          subtitle="Long form text that provides detailed information about the experience."
        />
      </BubbleFormGroup>
      <div className="col-span-2">
        <BubbleCheckbox
          label="Require at least one addon in guest checkout"
          formik={[formik, 'reservables.requireAtLeastOneAddon']}
          readOnly={readOnly}
          checked={!!values.reservables.requireAtLeastOneAddon}
          subtitle="When enabled, guests must select at least one addon before continuing from the Addons step in embed checkout."
        />
      </div>
      <BubbleFormGroup label="What's included">
        {readOnly ? (
          <div className="flex flex-wrap gap-1">
            {values.reservables.included.length > 0 ? (
              values.reservables.included.map(ri => (
                <BubbleBadge key={ri._id} variant="emerald">
                  {ri.name}
                </BubbleBadge>
              ))
            ) : (
              <span className="text-smoke italic">None</span>
            )}
          </div>
        ) : (
          <BubbleTypeahead
            block
            options={includedOpts}
            placeholder="E.g. 'Guided tour', 'Equipment rental', 'Free drinks', etc."
            menuClassName="max-h-80 overflow-y-auto"
            allowNew
            noSuggestionsClick
            alwaysShowSuggestions={includedOpts.length > 0}
            onReorder={newOrder =>
              setFieldValue(
                'reservables.included',
                newOrder.map(opt => opt.item)
              )
            }
            suggestionsText="No similar included found"
            suggestionClassName="p-1-i"
            suggestionsRenderFn={(_, opt) => (
              <div className="flex items-center">
                <BubbleInput
                  type="text"
                  block
                  appearance="bare"
                  value={opt.item.name}
                  className="flex-grow"
                  inputClassName="px-3 py-1 rounded"
                  onValue={val => {
                    const riIdx = values.reservables.included.findIndex(
                      ri => ri._id === opt.item._id
                    );
                    if (riIdx < 0) return;
                    const newIncluded = [...values.reservables.included];
                    newIncluded[riIdx].name = val;
                    setFieldValue('reservables.included', newIncluded);
                  }}
                />
              </div>
            )}
            onCreate={val =>
              setFieldValue('reservables.included', [
                ...values.reservables.included,
                { _id: new ObjectId().toHexString(), name: val },
              ])
            }
            onTrash={val =>
              setFieldValue(
                'reservables.included',
                values.reservables.included.filter(ri => ri._id !== val.item._id)
              )
            }
          />
        )}
      </BubbleFormGroup>
      <BubbleFormGroup label="What's not included">
        {readOnly ? (
          <div className="flex flex-wrap gap-1">
            {values.reservables.excluded.length > 0 ? (
              values.reservables.excluded.map(re => (
                <BubbleBadge key={re._id} variant="crimson">
                  {re.name}
                </BubbleBadge>
              ))
            ) : (
              <span className="text-smoke italic">None</span>
            )}
          </div>
        ) : (
          <BubbleTypeahead
            block
            options={excludedOpts}
            placeholder="E.g. 'Transportation', 'Meals', 'Gratuity', etc."
            menuClassName="max-h-80 overflow-y-auto"
            allowNew
            noSuggestionsClick
            alwaysShowSuggestions={excludedOpts.length > 0}
            onReorder={newOrder =>
              setFieldValue(
                'reservables.excluded',
                newOrder.map(opt => opt.item)
              )
            }
            suggestionsText="No similar excluded found"
            suggestionClassName="p-1-i"
            suggestionsRenderFn={(_, opt) => (
              <div className="flex items-center">
                <BubbleInput
                  type="text"
                  block
                  appearance="bare"
                  value={opt.item.name}
                  className="flex-grow"
                  inputClassName="px-3 py-1 rounded"
                  onValue={val => {
                    const reIdx = values.reservables.excluded.findIndex(
                      re => re._id === opt.item._id
                    );
                    if (reIdx < 0) return;
                    const newExcluded = [...values.reservables.excluded];
                    newExcluded[reIdx].name = val;
                    setFieldValue('reservables.excluded', newExcluded);
                  }}
                />
              </div>
            )}
            onCreate={val =>
              setFieldValue('reservables.excluded', [
                ...values.reservables.excluded,
                { _id: new ObjectId().toHexString(), name: val },
              ])
            }
            onTrash={val =>
              setFieldValue(
                'reservables.excluded',
                values.reservables.excluded.filter(re => re._id !== val.item._id)
              )
            }
          />
        )}
      </BubbleFormGroup>
      <BubbleFormGroup label="Meeting Point">
        <Editor
          textareaName="reservables.meetingPoint"
          value={values.reservables.meetingPoint || ''}
          onValue={val => {
            handleChange({
              target: {
                name: `reservables.meetingPoint`,
                value: val,
              },
            });
          }}
          onBlur={evt => {
            evt.target.name = 'reservables.meetingPoint';
            handleBlur(evt);
          }}
          mode="text-link"
          disabled={readOnly}
          subtitle="The location where the meeting will take place."
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Activity Location">
        <Editor
          textareaName="reservables.activityLocation"
          value={values.reservables.activityLocation || ''}
          onValue={val => {
            handleChange({
              target: {
                name: `reservables.activityLocation`,
                value: val,
              },
            });
          }}
          onBlur={evt => {
            evt.target.name = 'reservables.activityLocation';
            handleBlur(evt);
          }}
          mode="text-link"
          disabled={readOnly}
          subtitle="The location where the activity will take place."
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Reservation Questions" className="col-span-2">
        <ReservableQuestionFields
          readOnly={readOnly}
          formik={formik}
          fields={values.reservableFields}
          fieldPathPrefix="reservableFields"
          errors={reservableFieldErrors}
          touched={reservableFieldTouched}
          onUpdate={updatedFields => setFieldValue('reservableFields', updatedFields)}
        />
      </BubbleFormGroup>
      <div className="col-span-2 grid grid-cols-2 gap-4">
        <BubbleFormGroup label="Gratuity Fee (%)">
          <div className="flex items-start">
            <BubblePercentage
              value={values.reservables.gratuityFeePercentage}
              name="reservables.gratuityFeePercentage"
              readOnly={readOnly}
              onChange={handleChange}
              onBlur={handleBlur}
              block
              className="flex-grow"
              placeholder="E.g. '10%'"
              subtitle="A fixed percentage added to each experience for service fees."
            />
          </div>
        </BubbleFormGroup>
        <BubbleFormGroup label="Gratuity Fee ($)">
          <div className="flex items-start">
            <BubbleCurrency
              block
              readOnly={readOnly}
              name="reservables.gratuityFeeFlatAmountCents"
              className="flex-grow"
              value={values.reservables.gratuityFeeFlatAmountCents}
              onValue={val => setFieldValue('reservables.gratuityFeeFlatAmountCents', val || 0)}
              onBlur={handleBlur}
              placeholder="E.g. '$15.00'"
              subtitle="A fixed dollar amount added to each experience for service fees."
            />
          </div>
        </BubbleFormGroup>
        <BubbleFormGroup label="Host Revenue Split (%)">
          <BubblePercentage
            value={values.reservables.hostPercentage}
            name="reservables.hostPercentage"
            readOnly={readOnly}
            onChange={handleChange}
            onBlur={handleBlur}
            block
            className="flex-grow"
            placeholder="E.g. '80%'"
            subtitle="The portion of the net revenue paid out to the host."
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Tax Code">
          <BubbleHintSelect
            value={values.reservables.taxCode}
            name="reservables.taxCode"
            allowUndefined
            labelExtractor={tc => STRIPE_TAX_CODES_MAP[tc].name}
            options={STRIPE_TAX_CODES.map(tc => tc.id)}
            readOnly={readOnly}
            onValue={c => setFieldValue('reservables.taxCode', c)}
            onBlur={handleBlur}
            menuClassName="max-h-80 overflow-y-auto"
            block
            className="flex-grow"
            placeholder="Search..."
            subtitle="Select the accounting category that determines the tax rate for this item."
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Booking Window (Days Before)">
          <BubbleInput
            type="number"
            block
            className="flex-grow"
            name="reservables.bookingWindowDays"
            readOnly={readOnly}
            min={0}
            value={values.reservables.bookingWindowDays}
            onValue={val => setFieldValue('reservables.bookingWindowDays', val || undefined)}
            onBlur={handleBlur}
            placeholder="E.g. '2'"
            subtitle="Minimum number of days in advance a booking must be made. Leave blank for no restriction."
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Booking Opens (Days Before)">
          <BubbleInput
            type="number"
            block
            className="flex-grow"
            name="reservables.bookingOpenDays"
            readOnly={readOnly}
            min={0}
            value={values.reservables.bookingOpenDays}
            onValue={val => setFieldValue('reservables.bookingOpenDays', val || undefined)}
            onBlur={handleBlur}
            placeholder="E.g. '90'"
            subtitle="Maximum number of days in advance a booking can be made. Leave blank for no restriction."
          />
        </BubbleFormGroup>
      </div>
      <BubbleFormGroup label="Disclaimer" className="col-span-2">
        <Editor
          textareaName="reservables.disclaimer"
          value={values.reservables.disclaimer || ''}
          onValue={val => {
            handleChange({
              target: {
                name: `reservables.disclaimer`,
                value: val,
              },
            });
          }}
          onBlur={evt => {
            evt.target.name = 'reservables.disclaimer';
            handleBlur(evt);
          }}
          mode="text-link"
          disabled={readOnly}
          subtitle="The fine print that will be shown to guests during the booking process."
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Instructions" className="col-span-2">
        <Editor
          textareaName="reservables.instructions"
          value={values.reservables.instructions || ''}
          onValue={val => {
            handleChange({
              target: {
                name: `reservables.instructions`,
                value: val,
              },
            });
          }}
          onBlur={evt => {
            evt.target.name = 'reservables.instructions';
            handleBlur(evt);
          }}
          mode="text-link"
          disabled={readOnly}
          subtitle="Additional instructions shown in confirmation emails after the disclaimer."
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Notifications" className="col-span-2">
        <div className="bg-white p-4 border rounded shadow grid grid-cols-2 gap-4 justify-items-start">
          <BubbleFormGroup label="Today's Reservations" className="w-full">
            <BubbleTypeahead
              block
              options={instantEmailOpts}
              placeholder="E.g. 'john@example.com'"
              allowNew
              noSuggestionsClick
              readOnly={readOnly}
              alwaysShowSuggestions={instantEmailOpts.length > 0}
              suggestionsText="No emails added"
              suggestionClassName="p-1-i"
              suggestionsRenderFn={(_, opt) => (
                <div className="flex items-center">
                  <BubbleInput
                    type="text"
                    block
                    appearance="bare"
                    value={opt.item}
                    className="flex-grow"
                    inputClassName="px-3 py-1 rounded"
                    onValue={val => {
                      const emails = [...(values.reservables.emailSettings?.instantEmails ?? [])];
                      const idx = emails.indexOf(opt.item);
                      if (idx < 0) return;
                      emails[idx] = val;
                      setFieldValue('reservables.emailSettings.instantEmails', emails);
                    }}
                  />
                </div>
              )}
              onCreate={val =>
                setFieldValue('reservables.emailSettings.instantEmails', [
                  ...(values.reservables.emailSettings?.instantEmails ?? []),
                  val,
                ])
              }
              onTrash={val =>
                setFieldValue(
                  'reservables.emailSettings.instantEmails',
                  (values.reservables.emailSettings?.instantEmails ?? []).filter(
                    e => e !== val.item
                  )
                )
              }
              subtitle="Notified immediately when a reservation is made for today."
            />
          </BubbleFormGroup>
          <BubbleFormGroup label="Daily Summary" className="w-full">
            <BubbleTypeahead
              block
              options={summaryEmailOpts}
              placeholder="E.g. 'john@example.com'"
              allowNew
              noSuggestionsClick
              readOnly={readOnly}
              alwaysShowSuggestions={summaryEmailOpts.length > 0}
              suggestionsText="No emails added"
              suggestionClassName="p-1-i"
              suggestionsRenderFn={(_, opt) => (
                <div className="flex items-center">
                  <BubbleInput
                    type="text"
                    block
                    appearance="bare"
                    value={opt.item}
                    className="flex-grow"
                    inputClassName="px-3 py-1 rounded"
                    onValue={val => {
                      const emails = [...(values.reservables.emailSettings?.summaryEmails ?? [])];
                      const idx = emails.indexOf(opt.item);
                      if (idx < 0) return;
                      emails[idx] = val;
                      setFieldValue('reservables.emailSettings.summaryEmails', emails);
                    }}
                  />
                </div>
              )}
              onCreate={val =>
                setFieldValue('reservables.emailSettings.summaryEmails', [
                  ...(values.reservables.emailSettings?.summaryEmails ?? []),
                  val,
                ])
              }
              onTrash={val =>
                setFieldValue(
                  'reservables.emailSettings.summaryEmails',
                  (values.reservables.emailSettings?.summaryEmails ?? []).filter(
                    e => e !== val.item
                  )
                )
              }
              subtitle="Receives an end-of-day summary of all reservations."
            />
          </BubbleFormGroup>
        </div>
      </BubbleFormGroup>
    </fieldset>
  );
}

interface EventTemplateFormikTicketsSubTabProps {
  formik: FormikProps<EventTemplateFormikForm>;
  experienceType: RESERVABLE_EXPERIENCE_TYPE;
  type: 'create' | 'edit' | 'view' | 'filter';
  readOnly?: boolean;
  onAddNewTickets?: (ticket: ReservableTicket[]) => void;
}

export function EventTemplateFormikTicketsSubTab(props: EventTemplateFormikTicketsSubTabProps) {
  const { readOnly, formik, experienceType } = props;
  const { values, handleChange, handleBlur, setFieldValue, errors, touched } = formik;
  const { activeProperty } = useActiveProperty();

  const expTypeLabel = experienceIsRental(experienceType) ? 'Rental' : 'Ticket';

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({
    // [values.reservableTickets[0]?._id || '']: true,
  });
  const [showCopy, setShowCopy] = useState(false);

  function addNewTicket() {
    const newExpanded = { ...expanded };
    const newTicket = createDefaultTicket();
    values.reservableTickets.forEach(t => {
      newExpanded[t._id] = false;
    });
    newExpanded[newTicket._id] = true;

    setExpanded(newExpanded);
    setFieldValue('reservableTickets', [...values.reservableTickets, newTicket]);
    props.onAddNewTickets?.([newTicket]);
  }

  function cloneTickets(tickets: ReservableTicket[]) {
    const newExpanded = { ...expanded };
    const newTickets = tickets.map(ticket => ({
      ...ticket,
      _id: new ObjectId().toHexString(),
      name: `${ticket.name}`,
    }));

    values.reservableTickets.forEach(t => {
      newExpanded[t._id] = false;
    });

    setExpanded(newExpanded);
    setFieldValue('reservableTickets', [...values.reservableTickets, ...newTickets]);
    props.onAddNewTickets?.(newTickets);
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      {values.reservableTickets.length < 1 && (
        <div
          className="p-8 text-center min-h-56 flex flex-col items-center justify-center border border-smoke-lightest rounded-lg"
          style={{ borderStyle: 'dashed', borderSpacing: '1rem', borderWidth: '3px' }}
        >
          <fieldset disabled={readOnly} className="text-center grid grid-cols-1 gap-4">
            <div>
              <div className="text-lg font-semibold text-slate mb-1">
                {expTypeLabel} list is empty
              </div>
              <div className="text-smoke">Add {expTypeLabel}s to this experience</div>
            </div>
            <BubbleButton
              icon="plus"
              variant="navy"
              onClick={addNewTicket}
              className="border border-navy min-w-0"
              style={{ justifySelf: 'center' }}
            >
              Add your First {expTypeLabel}
            </BubbleButton>
          </fieldset>
        </div>
      )}
      {values.reservableTickets.map((ticket, i) => {
        const hasErrors = Object.keys((errors.reservableTickets?.[i] as {}) ?? {}).length > 0;

        return (
          <div key={ticket._id} className="fragment-block">
            <div className="fb-header relative border-b">
              <div className="flex items-center py-4 pl-6 pr-4">
                <div
                  className={`flex-grow relative flex items-center ${
                    hasErrors ? 'text-crimson' : 'text-navy'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={
                      hasErrors
                        ? 'triangle-exclamation'
                        : experienceIsRental(experienceType)
                        ? 'diamonds-4'
                        : 'ticket'
                    }
                    fixedWidth
                    className="text-xl ml-2 mr-6"
                  />
                  <div className="py-1">
                    <div className="text-lg font-semibold">{ticket.name || expTypeLabel}</div>
                    <div className="text-xs flex items-center space-x-2 text-slate">
                      <span>{formatCentsToUSD(ticket.price) || 'Invalid Price'}</span>
                      <FontAwesomeIcon icon="pipe" />
                      <span>{ticket.fields.length} Question(s)</span>
                    </div>
                  </div>
                  <BubbleButton
                    className="ml-auto stretched-link p-3-i"
                    onClick={() => {
                      setExpanded({
                        ...expanded,
                        [ticket._id]: !expanded[ticket._id],
                      });
                    }}
                    variant="navy"
                    inverse
                    icon="edit"
                  />
                </div>
                {!readOnly && (
                  <div>
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        if (i === 0) return;
                        const newTickets = [...values.reservableTickets];
                        const temp = newTickets[i - 1];
                        newTickets[i - 1] = newTickets[i];
                        newTickets[i] = temp;
                        setFieldValue('reservableTickets', newTickets);
                      }}
                      variant="slate"
                      inverse
                      icon="arrow-up"
                      disabled={i === 0}
                    />
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        if (i === values.reservableTickets.length - 1) return;
                        const newTickets = [...values.reservableTickets];
                        const temp = newTickets[i];
                        newTickets[i] = newTickets[i + 1];
                        newTickets[i + 1] = temp;
                        setFieldValue('reservableTickets', newTickets);
                      }}
                      variant="slate"
                      inverse
                      icon="arrow-down"
                      disabled={i === values.reservableTickets.length - 1}
                    />
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        const newTickets = [...values.reservableTickets];
                        newTickets.splice(i, 1);
                        setFieldValue('reservableTickets', newTickets);
                      }}
                      variant="crimson"
                      inverse
                      icon="trash-alt"
                    />
                  </div>
                )}
              </div>
            </div>
            {expanded[ticket._id] && (
              <fieldset disabled={readOnly} className="p-6 grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <BubbleFormGroup label="Images" className="col-span-2">
                    <BubbleImageSelector
                      maxItems={3}
                      readOnly={readOnly}
                      value={ticket.images || []}
                      onValue={images => {
                        const newTickets = [...values.reservableTickets];
                        newTickets[i].images = images;
                        setFieldValue('reservableTickets', newTickets);
                      }}
                    />
                  </BubbleFormGroup>
                  <BubbleFormGroup
                    label="Name"
                    error={
                      ['edit', 'create'].includes(props.type) &&
                      fme(errors.reservableTickets?.[i], touched.reservableTickets?.[i], 'name')
                    }
                    asterisk
                  >
                    <BubbleInput
                      block
                      type="text"
                      formik={[formik, `reservableTickets[${i}].name`]}
                      readOnly={readOnly}
                      value={ticket.name}
                      placeholder="E.g. 'Adult (12+)', 'General Admission', etc."
                    />
                  </BubbleFormGroup>
                  <BubbleFormGroup
                    label="Price"
                    error={
                      ['edit', 'create'].includes(props.type) &&
                      fme(errors.reservableTickets?.[i], touched.reservableTickets?.[i], 'price')
                    }
                    asterisk
                  >
                    <BubbleCurrency
                      block
                      name={`reservableTickets[${i}].price`}
                      readOnly={readOnly}
                      value={ticket.price}
                      onValue={val => setFieldValue(`reservableTickets[${i}].price`, val)}
                      onBlur={formik.handleBlur}
                      placeholder="E.g. '$45.00'"
                    />
                  </BubbleFormGroup>
                </div>
                <BubbleFormGroup label="Limit Per Reservation">
                  <BubbleInput
                    block
                    type="number"
                    name={`reservableTickets[${i}].limitPerReservation`}
                    readOnly={readOnly}
                    value={ticket.limitPerReservation}
                    onValue={val =>
                      setFieldValue(`reservableTickets[${i}].limitPerReservation`, val || undefined)
                    }
                    min={0}
                    onBlur={formik.handleBlur}
                    placeholder="E.g. '4'"
                    subtitle="Max quantity of this ticket a single reservation can include. Leave blank for no limit."
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Description">
                  <Editor
                    textareaName={`reservableTickets[${i}].description`}
                    value={ticket.description || ''}
                    onValue={val => {
                      handleChange({
                        target: {
                          name: `reservableTickets[${i}].description`,
                          value: val,
                        },
                      });
                    }}
                    onBlur={evt => {
                      evt.target.name = `reservableTickets[${i}].description`;
                      handleBlur(evt);
                    }}
                    mode="text-link"
                    disabled={readOnly}
                    subtitle="Describe this ticket type to help guests understand what they are purchasing."
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label={`${expTypeLabel} Questions`}>
                  <ReservableTicketFields
                    readOnly={readOnly}
                    formik={formik}
                    ticketIdx={i}
                    onUpdate={updatedFields => {
                      const newTickets = [...values.reservableTickets];
                      newTickets.splice(i, 1, {
                        ...newTickets[i],
                        fields: updatedFields,
                      });
                      // newTickets[i].fields = updatedFields;
                      setFieldValue('reservableTickets', newTickets);
                    }}
                  />
                </BubbleFormGroup>
              </fieldset>
            )}
          </div>
        );
      })}
      {!props.readOnly && (
        <>
          {values.reservableTickets.length > 0 && (
            <BubbleButton
              icon="plus"
              variant="navy"
              block
              inverse
              onClick={addNewTicket}
              disabled={readOnly}
              className="border border-navy"
            >
              Add Another {expTypeLabel}
            </BubbleButton>
          )}
          <BubbleButton
            disabled={readOnly}
            variant="navy"
            inverse
            icon="copy"
            onClick={() => setShowCopy(true)}
          >
            Copy From Another Experience
          </BubbleButton>
          {showCopy && (
            <ReserveCopyFromExperience<ReservableTicket>
              title="Tickets"
              propertyId={activeProperty._id}
              selector={e => e.reservables.tickets}
              onCopy={cloneTickets}
              onClose={() => setShowCopy(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

interface EventTemplateFormikPackagesSubTabProps {
  formik: FormikProps<EventTemplateFormikForm>;
  type: 'create' | 'edit' | 'view' | 'filter';
  readOnly?: boolean;
  onAddNewPackages?: (pkgs: ReservablePackage[]) => void;
  onSkip?: () => void;
}

export function EventTemplateFormikPackagesSubTab(props: EventTemplateFormikPackagesSubTabProps) {
  const { readOnly, formik } = props;
  const { values, setFieldValue, errors, touched, handleBlur, handleChange } = formik;
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({
    // [values.reservablePackages[0]?._id || '']: true,
  });
  const [showCopy, setShowCopy] = useState(false);

  function addNewPackage() {
    const newExpanded = { ...expanded };
    const newPackage = createDefaultPackage();

    values.reservablePackages.forEach(t => {
      newExpanded[t._id] = false;
    });
    newExpanded[newPackage._id] = true;

    setExpanded(newExpanded);
    setFieldValue('reservablePackages', [...values.reservablePackages, newPackage]);
    props.onAddNewPackages?.([newPackage]);
  }

  function clonePackages(packages: ReservablePackage[]) {
    const newExpanded = { ...expanded };
    const newPackages = packages.map(pkg => ({
      ...pkg,
      _id: new ObjectId().toHexString(),
      name: `${pkg.name}`,
    }));

    values.reservablePackages.forEach(t => {
      newExpanded[t._id] = false;
    });

    setExpanded(newExpanded);
    setFieldValue('reservablePackages', [...values.reservablePackages, ...newPackages]);
    props.onAddNewPackages?.(newPackages);
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      {values.reservablePackages.length < 1 && (
        <div
          className="p-8 text-center min-h-56 flex flex-col items-center justify-center border border-smoke-lightest rounded-lg"
          style={{ borderStyle: 'dashed', borderSpacing: '1rem', borderWidth: '3px' }}
        >
          <fieldset disabled={readOnly} className="text-center grid grid-cols-1 gap-4">
            <div>
              <div className="text-lg font-semibold text-slate mb-1">Package list is empty</div>
              <div className="text-smoke">Add packages to this experience</div>
            </div>
            <div className="grid grid-cols-1">
              <BubbleButton
                icon="plus"
                variant="navy"
                onClick={addNewPackage}
                className="border border-navy min-w-0"
                style={{ justifySelf: 'center' }}
              >
                Add your First Package
              </BubbleButton>
              {props.onSkip && (
                <BubbleButton variant="smoke" inverse onClick={props.onSkip}>
                  Or, Skip This Step
                </BubbleButton>
              )}
            </div>
          </fieldset>
        </div>
      )}
      {values.reservablePackages.map((pkg, i) => {
        const hasErrors = Object.keys((errors.reservablePackages?.[i] as {}) ?? {}).length > 0;

        return (
          <div key={pkg._id} className="fragment-block">
            <div className="fb-header relative border-b">
              <div className="flex items-center py-4 pl-6 pr-4">
                <div
                  className={`flex-grow relative flex items-center ${
                    hasErrors ? 'text-crimson' : 'text-navy'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={hasErrors ? 'exclamation-triangle' : 'box-open'}
                    fixedWidth
                    className="text-xl ml-2 mr-6"
                  />
                  <div className="py-1">
                    <div className="text-lg font-semibold">{pkg.name || 'Package'}</div>
                    <div className="text-xs flex items-center space-x-2 text-slate">
                      <span>{formatCentsToUSD(pkg.price) || 'Invalid Price'}</span>
                      <FontAwesomeIcon icon="pipe" />
                      <span>{pkg.images.length} Image(s)</span>
                    </div>
                  </div>
                  <BubbleButton
                    className="ml-auto stretched-link p-3-i"
                    onClick={() => {
                      setExpanded({
                        ...expanded,
                        [pkg._id]: !expanded[pkg._id],
                      });
                    }}
                    variant="navy"
                    inverse
                    icon="edit"
                  />
                </div>
                {!readOnly && (
                  <div>
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        if (i === 0) return;
                        const newPackages = [...values.reservablePackages];
                        const temp = newPackages[i - 1];
                        newPackages[i - 1] = newPackages[i];
                        newPackages[i] = temp;
                        setFieldValue('reservablePackages', newPackages);
                      }}
                      variant="slate"
                      inverse
                      icon="arrow-up"
                      disabled={i === 0}
                    />
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        if (i === values.reservablePackages.length - 1) return;
                        const newPackages = [...values.reservablePackages];
                        const temp = newPackages[i];
                        newPackages[i] = newPackages[i + 1];
                        newPackages[i + 1] = temp;
                        setFieldValue('reservablePackages', newPackages);
                      }}
                      variant="slate"
                      inverse
                      icon="arrow-down"
                      disabled={i === values.reservablePackages.length - 1}
                    />
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        const newPackages = [...values.reservablePackages];
                        newPackages.splice(i, 1);
                        setFieldValue('reservablePackages', newPackages);
                      }}
                      variant="crimson"
                      inverse
                      icon="trash-alt"
                    />
                  </div>
                )}
              </div>
            </div>
            {expanded[pkg._id] && (
              <fieldset disabled={readOnly} className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <BubbleFormGroup label="Images" className="col-span-2">
                    <BubbleImageSelector
                      maxItems={3}
                      readOnly={readOnly}
                      value={pkg.images || []}
                      onValue={images => {
                        const newPackages = [...values.reservablePackages];
                        newPackages[i].images = images;
                        setFieldValue('reservablePackages', newPackages);
                      }}
                    />
                  </BubbleFormGroup>
                  <BubbleFormGroup
                    label="Name"
                    error={
                      ['edit', 'create'].includes(props.type) &&
                      fme(errors.reservablePackages?.[i], touched.reservablePackages?.[i], 'name')
                    }
                    asterisk
                  >
                    <BubbleInput
                      block
                      type="text"
                      placeholder="E.g. 'VIP Couples Bundle', 'Family Pack', etc."
                      formik={[formik, `reservablePackages[${i}].name`]}
                      readOnly={readOnly}
                      value={pkg.name}
                    />
                  </BubbleFormGroup>
                  <BubbleFormGroup
                    label="Price"
                    error={
                      ['edit', 'create'].includes(props.type) &&
                      fme(errors.reservablePackages?.[i], touched.reservablePackages?.[i], 'price')
                    }
                    asterisk
                  >
                    <BubbleCurrency
                      block
                      name={`reservablePackages[${i}].price`}
                      placeholder="E.g. '$45.00'"
                      readOnly={readOnly}
                      value={pkg.price}
                      onValue={val => setFieldValue(`reservablePackages[${i}].price`, val)}
                      onBlur={handleBlur}
                    />
                  </BubbleFormGroup>
                  {user.superAdmin && (
                    <BubbleFormGroup label="Tax Code">
                      <BubbleHintSelect
                        value={pkg.taxCode}
                        name={`reservablePackages[${i}].taxCode`}
                        allowUndefined
                        labelExtractor={tc => STRIPE_TAX_CODES_MAP[tc].name}
                        options={STRIPE_TAX_CODES.map(tc => tc.id)}
                        readOnly={readOnly}
                        onValue={c => setFieldValue(`reservablePackages[${i}].taxCode`, c)}
                        onBlur={handleBlur}
                        menuClassName="max-h-80 overflow-y-auto"
                        block
                        className="flex-grow"
                        placeholder="Search..."
                        subtitle="Select the accounting category that determines the tax rate for this item."
                      />
                    </BubbleFormGroup>
                  )}
                  <BubbleCheckbox
                    label="Price is Per Ticket"
                    formik={[formik, `reservablePackages[${i}].pricePerTicket`]}
                    readOnly={readOnly}
                    checked={!!pkg.pricePerTicket}
                    subtitle="Check this if the price should be multiplied by the quantity of tickets being purchased."
                  />
                  <BubbleFormGroup label="Description" className="col-span-2">
                    <Editor
                      textareaName={`reservablePackages[${i}].description`}
                      value={pkg.description || ''}
                      onValue={val => {
                        handleChange({
                          target: {
                            name: `reservablePackages[${i}].description`,
                            value: val,
                          },
                        });
                      }}
                      onBlur={evt => {
                        evt.target.name = `reservablePackages[${i}].description`;
                        handleBlur(evt);
                      }}
                      mode="text-link"
                      disabled={readOnly}
                      subtitle="Describe what is physically bundled in this package to help guests to understand what they are purchasing."
                    />
                  </BubbleFormGroup>
                </div>
              </fieldset>
            )}
          </div>
        );
      })}
      {!props.readOnly && (
        <>
          {values.reservablePackages.length > 0 && (
            <BubbleButton
              icon="plus"
              variant="navy"
              block
              inverse
              onClick={addNewPackage}
              disabled={readOnly}
              className="border border-navy"
            >
              Add Another Package
            </BubbleButton>
          )}
          <BubbleButton
            disabled={readOnly}
            variant="navy"
            inverse
            icon="copy"
            onClick={() => setShowCopy(true)}
          >
            Copy From Another Experience
          </BubbleButton>
          {showCopy && (
            <ReserveCopyFromExperience<ReservablePackage>
              title="Packages"
              propertyId={activeProperty._id}
              selector={e => e.reservables.packages}
              onCopy={clonePackages}
              onClose={() => setShowCopy(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

interface EventTemplateFormikAddonsSubTabProps {
  formik: FormikProps<EventTemplateFormikForm>;
  type: 'create' | 'edit' | 'view' | 'filter';
  readOnly?: boolean;
  onAddNewAddons?: (addons: ReservableAddon[]) => void;
  onSkip?: () => void;
}

export function EventTemplateFormikAddonsSubTab(props: EventTemplateFormikAddonsSubTabProps) {
  const { readOnly, formik } = props;
  const { values, setFieldValue, handleBlur, handleChange } = formik;
  const { errors, touched } = formik;
  const { setVerifyClose } = useBubbleDrawerTools();
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({
    // [values.reservableAddons[0]?._id || '']: true,
  });
  const [showCopy, setShowCopy] = useState(false);

  function addNewAddon() {
    const newExpanded = { ...expanded };
    const newAddon = createDefaultAddon();

    values.reservableAddons.forEach(t => {
      newExpanded[t._id] = false;
    });
    newExpanded[newAddon._id] = true;

    setExpanded(newExpanded);
    setFieldValue('reservableAddons', [...values.reservableAddons, newAddon]);
  }

  function cloneAddons(addons: ReservableAddon[]) {
    const newExpanded = { ...expanded };
    const newAddons = addons.map(addon => ({
      ...addon,
      _id: new ObjectId().toHexString(),
      name: `${addon.name}`,
    }));

    values.reservableAddons.forEach(t => {
      newExpanded[t._id] = false;
    });

    setExpanded(newExpanded);
    setFieldValue('reservableAddons', [...values.reservableAddons, ...newAddons]);
    props.onAddNewAddons?.(newAddons);
  }

  useEffect(() => {
    setVerifyClose(formik.dirty);
  }, [formik.dirty]);

  return (
    <div className="grid grid-cols-1 gap-2">
      {values.reservableAddons.length < 1 && (
        <div
          className="p-8 text-center min-h-56 flex flex-col items-center justify-center border border-smoke-lightest rounded-lg"
          style={{ borderStyle: 'dashed', borderSpacing: '1rem', borderWidth: '3px' }}
        >
          <fieldset disabled={readOnly} className="text-center grid grid-cols-1 gap-4">
            <div>
              <div className="text-lg font-semibold text-slate mb-1">Addon list is empty</div>
              <div className="text-smoke">Add addons to this experience</div>
            </div>
            <div className="grid grid-cols-1">
              <BubbleButton
                icon="plus"
                variant="navy"
                onClick={addNewAddon}
                className="border border-navy min-w-0"
                style={{ justifySelf: 'center' }}
              >
                Add your First Addon
              </BubbleButton>
              {props.onSkip && (
                <BubbleButton variant="smoke" inverse onClick={props.onSkip}>
                  Or, Skip This Step
                </BubbleButton>
              )}
            </div>
          </fieldset>
        </div>
      )}
      {values.reservableAddons.map((addon, i) => {
        const hasErrors = Object.keys((errors.reservableAddons?.[i] as {}) ?? {}).length > 0;

        return (
          <div key={addon._id} className="fragment-block">
            <div className="fb-header relative border-b">
              <div className="flex items-center py-4 pl-6 pr-4">
                <div
                  className={`flex-grow relative flex items-center ${
                    hasErrors ? 'text-crimson' : 'text-navy'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={hasErrors ? 'exclamation-triangle' : 'puzzle-piece'}
                    fixedWidth
                    className="text-xl ml-2 mr-6"
                  />
                  <div className="py-1">
                    <div className="text-lg font-semibold">{addon.name || 'Addon'}</div>
                    <div className="text-xs flex items-center space-x-2 text-slate">
                      <span>{formatCentsToUSD(addon.price) || 'Invalid Price'}</span>
                      <FontAwesomeIcon icon="pipe" />
                      <span>{addon.images.length} Image(s)</span>
                    </div>
                  </div>
                  <BubbleButton
                    className="ml-auto stretched-link p-3-i"
                    onClick={() => {
                      setExpanded({
                        ...expanded,
                        [addon._id]: !expanded[addon._id],
                      });
                    }}
                    variant="navy"
                    inverse
                    icon="edit"
                  />
                </div>
                {!readOnly && (
                  <div>
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        if (i === 0) return;
                        const newAddons = [...values.reservableAddons];
                        const temp = newAddons[i - 1];
                        newAddons[i - 1] = newAddons[i];
                        newAddons[i] = temp;
                        setFieldValue('reservableAddons', newAddons);
                      }}
                      variant="slate"
                      inverse
                      icon="arrow-up"
                      disabled={i === 0}
                    />
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        if (i === values.reservableAddons.length - 1) return;
                        const newAddons = [...values.reservableAddons];
                        const temp = newAddons[i];
                        newAddons[i] = newAddons[i + 1];
                        newAddons[i + 1] = temp;
                        setFieldValue('reservableAddons', newAddons);
                      }}
                      variant="slate"
                      inverse
                      icon="arrow-down"
                      disabled={i === values.reservableAddons.length - 1}
                    />
                    <BubbleButton
                      className="p-3-i"
                      onClick={() => {
                        const newAddons = [...values.reservableAddons];
                        newAddons.splice(i, 1);
                        setFieldValue('reservableAddons', newAddons);
                      }}
                      variant="crimson"
                      inverse
                      icon="trash-alt"
                    />
                  </div>
                )}
              </div>
            </div>
            {expanded[addon._id] && (
              <fieldset disabled={readOnly} className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <BubbleFormGroup label="Images" className="col-span-2">
                    <BubbleImageSelector
                      maxItems={3}
                      readOnly={readOnly}
                      value={addon.images || []}
                      onValue={images => {
                        const newAddons = [...values.reservableAddons];
                        newAddons[i].images = images;
                        setFieldValue('reservableAddons', newAddons);
                      }}
                    />
                  </BubbleFormGroup>
                  <BubbleFormGroup
                    label="Name"
                    error={
                      ['edit', 'create'].includes(props.type) &&
                      fme(errors.reservableAddons?.[i], touched.reservableAddons?.[i], 'name')
                    }
                    asterisk
                  >
                    <BubbleInput
                      block
                      type="text"
                      placeholder="E.g. 'Water Bottle', 'Chips', etc."
                      formik={[formik, `reservableAddons[${i}].name`]}
                      readOnly={readOnly}
                      value={addon.name}
                    />
                  </BubbleFormGroup>
                  <BubbleFormGroup
                    label="Price"
                    error={
                      ['edit', 'create'].includes(props.type) &&
                      fme(errors.reservableAddons?.[i], touched.reservableAddons?.[i], 'price')
                    }
                  >
                    <BubbleCurrency
                      block
                      name={`reservableAddons[${i}].price`}
                      placeholder="E.g. '$5.00'"
                      readOnly={readOnly}
                      value={addon.price}
                      onValue={val => setFieldValue(`reservableAddons[${i}].price`, val)}
                      onBlur={handleBlur}
                    />
                  </BubbleFormGroup>
                  {user.superAdmin && (
                    <BubbleFormGroup label="Tax Code">
                      <BubbleHintSelect
                        value={addon.taxCode}
                        name={`reservableAddons[${i}].taxCode`}
                        allowUndefined
                        labelExtractor={tc => STRIPE_TAX_CODES_MAP[tc].name}
                        options={STRIPE_TAX_CODES.map(tc => tc.id)}
                        readOnly={readOnly}
                        onValue={c => setFieldValue(`reservableAddons[${i}].taxCode`, c)}
                        onBlur={handleBlur}
                        menuClassName="max-h-80 overflow-y-auto"
                        block
                        className="flex-grow"
                        placeholder="Search..."
                        subtitle="Select the accounting category that determines the tax rate for this item."
                      />
                    </BubbleFormGroup>
                  )}
                  <BubbleCheckbox
                    label="Price is Per Ticket"
                    formik={[formik, `reservableAddons[${i}].pricePerTicket`]}
                    readOnly={readOnly}
                    checked={!!addon.pricePerTicket}
                    subtitle="Check this if the price should be multiplied by the quantity of tickets being purchased."
                  />
                  <BubbleFormGroup label="Description" className="col-span-2">
                    <Editor
                      textareaName={`reservableAddons[${i}].description`}
                      value={addon.description || ''}
                      onValue={val => {
                        handleChange({
                          target: {
                            name: `reservableAddons[${i}].description`,
                            value: val,
                          },
                        });
                      }}
                      onBlur={evt => {
                        evt.target.name = `reservableAddons[${i}].description`;
                        handleBlur(evt);
                      }}
                      mode="text-link"
                      disabled={readOnly}
                      subtitle="Describe what is physically bundled in this addon to help guests to understand what they are purchasing."
                    />
                  </BubbleFormGroup>
                </div>
              </fieldset>
            )}
          </div>
        );
      })}
      {!props.readOnly && (
        <>
          {values.reservableAddons.length > 0 && (
            <BubbleButton
              icon="plus"
              variant="navy"
              inverse
              onClick={addNewAddon}
              className="border border-navy"
              disabled={readOnly}
            >
              Add Another Addon
            </BubbleButton>
          )}
          <BubbleButton
            disabled={readOnly}
            variant="navy"
            inverse
            icon="copy"
            onClick={() => setShowCopy(true)}
          >
            Copy From Another Experience
          </BubbleButton>
          {showCopy && (
            <ReserveCopyFromExperience<ReservableAddon>
              title="Addons"
              propertyId={activeProperty._id}
              selector={e => e.reservables.addons}
              onCopy={cloneAddons}
              onClose={() => setShowCopy(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

export default forwardRef<EventTemplateFormikRef, EventTemplateFormikProps>(EventTemplateFormik);
